import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { Firestore } from 'firebase/firestore';
import { useAuth0 } from '@auth0/auth0-react';
import { getAuth, onAuthStateChanged, signOut, type Auth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

import { getAuthUser, useAccessToken, useAccessTokenGetter } from '@flintmint/web-shared/hooks/use-auth-user';
import { retryWithBackoff } from '@flintmint/core-shared/utils';
import type { AuthDenialReason } from '@flintmint/core-shared/constants';
import { ApiService, getAuthDenialReason } from '@/services/api-service';
import { FirebaseService } from '@/services/firebase-service';
import { CallableApiService } from '@/services/callable-api-service';
import { capture } from '@/analytics';
import { appRoutes } from '@/constants/routes';

type ServicesState =
  | { status: 'pending'; firestore: null; callableApi: null; api: null; authDenial: null }
  | { status: 'ready'; firestore: Firestore; callableApi: CallableApiService; api: ApiService; authDenial: null }
  // A denial keeps firestore, callableApi and api only for a restored session refused for missing
  // agreements: the session stays signed in and replication keeps running behind the screen.
  | {
      status: 'denied';
      firestore: Firestore | null;
      callableApi: CallableApiService | null;
      api: ApiService | null;
      authDenial: AuthDenialReason;
    };

type Services = ServicesState & { acceptAgreements: () => Promise<void> };

const uninitialized: ServicesState = { status: 'pending', firestore: null, callableApi: null, api: null, authDenial: null };

const ServicesContext = createContext<Services>({
  ...uninitialized,
  acceptAgreements: () => Promise.reject(new Error('ServicesProvider is not mounted')),
});

export function ServicesProvider({ children }: { children: ReactNode }) {
  const accessToken = useAccessToken();
  const getAccessToken = useAccessTokenGetter();
  const { isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const [services, setServices] = useState<ServicesState>(uninitialized);
  const [initRun, setInitRun] = useState(0);

  useEffect(() => {
    if (!accessToken || !isAuthenticated) return;

    let superseded = false;

    async function initServices() {
      try {
        const firebaseService = new FirebaseService();
        const api = new ApiService(getAccessToken, reportAuthDenial);

        const auth = getAuth();
        await auth.authStateReady();

        const userId = getAuthUser().id;
        const restoredSession = await hasPersistedSessionFor(auth, userId);
        if (superseded) return;
        if (!restoredSession) {
          // Auth state is shared across tabs: signing out a same-user session would log out every
          // other open tab, and signInWithCustomToken replaces it in place.
          if (auth.currentUser && auth.currentUser.uid !== userId) await signOut(auth);
          await signInWithCustomTokenExchange(api, firebaseService);
          if (superseded) return;
        }

        // Only expose Firebase services AFTER auth completes
        const firestore = firebaseService.firestore;
        const callableApi = new CallableApiService(firebaseService.functions);
        setServices({ status: 'ready', firestore, callableApi, api, authDenial: null });

        if (restoredSession) {
          // A newer init run owns the state by the time a superseded check answers.
          refreshAccessInBackground(api, auth, (update) => {
            if (!superseded) setServices(update);
          });
        }
      } catch (error) {
        const authDenial = getAuthDenialReason(error);
        if (authDenial) {
          console.warn('Access denied during service initialization:', authDenial, error);
          if (!superseded) setServices({ status: 'denied', firestore: null, callableApi: null, api: null, authDenial });
        } else {
          console.error('Failed to initialize services:', error);
          if (!superseded) setServices(uninitialized);
        }
      }
    }

    initServices();

    return () => {
      superseded = true;
    };
  }, [accessToken, getAccessToken, isAuthenticated, initRun]);

  useEffect(() => {
    if (!services.firestore) return;

    // The SDK force-signs-out only on terminal conditions (refresh tokens revoked on role
    // shrink, account disabled/deleted) - the session is dead, so run the full logout flow
    // (wipes local RxDB and cached tokens, all tabs). A revoked user's next login lands on
    // /access-denied via the init denial path.
    const unsubscribe = onAuthStateChanged(getAuth(), (firebaseUser) => {
      if (!firebaseUser) navigate(appRoutes.logout);
    });
    return unsubscribe;
  }, [services.firestore, navigate]);

  useEffect(() => {
    // Without a live session there is nothing to act on: init is still running or already denied.
    if (!services.firestore) return;

    setAuthDenialListener((authDenial) => actOnAuthDenial(authDenial, getAuth(), setServices));
    return () => setAuthDenialListener(null);
  }, [services.firestore]);

  const acceptAgreements = async () => {
    try {
      await (services.api ?? new ApiService(getAccessToken, reportAuthDenial)).acceptAgreements();
    } catch (error) {
      // A denial is not a failed save: the init rerun below routes it like any other denial.
      if (!getAuthDenialReason(error)) throw error;
    }
    setInitRun((run) => run + 1);
  };

  return <ServicesContext.Provider value={{ ...services, acceptAgreements }}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  return useContext(ServicesContext);
}

let authDenialListener: ((authDenial: AuthDenialReason) => void) | null = null;

// For request paths outside React (the module-level agent session): a session that is already
// running only learns that its access changed from a refused request.
export function reportAuthDenial(authDenial: AuthDenialReason): void {
  authDenialListener?.(authDenial);
}

function setAuthDenialListener(listener: typeof authDenialListener): void {
  authDenialListener = listener;
}

// The one policy for a denial seen on a live session, whether the background access check or a
// refused request found it: sign the session out - the auth-state listener above then runs the
// full logout flow. Missing agreements are the exception: the session and its local data stay,
// and the agreements screen takes over.
function actOnAuthDenial(authDenial: AuthDenialReason, auth: Auth, setServices: SetServices) {
  if (authDenial === 'agreements_not_accepted') setServices(toAgreementsDenied);
  else signOut(auth).catch(console.warn);
}

type SetServices = (update: (current: ServicesState) => ServicesState) => void;

// Only a ready session has firestore, callableApi and api to keep; a session that is pending again or
// already denied has nothing for the agreements screen to take over.
function toAgreementsDenied(current: ServicesState): ServicesState {
  return current.status === 'ready' ? { ...current, status: 'denied', authDenial: 'agreements_not_accepted' } : current;
}

// Reusing a session is only safe when it carries the `roles` claim firestore.rules gate every
// read and write on - it is minted solely into the custom token at the /api/auth exchange, so a
// session persisted before that rollout would otherwise yield a ready app denied by every query.
async function hasPersistedSessionFor(auth: Auth, userId: string): Promise<boolean> {
  const user = auth.currentUser;
  if (!userId || user?.uid !== userId) return false;

  try {
    const { claims } = await user.getIdTokenResult();
    return Array.isArray(claims.roles) && claims.roles.includes('user');
  } catch {
    // The SDK clears currentUser before rethrowing terminal auth errors (revoked refresh token,
    // disabled account), so a surviving user means the read failed transiently - keep the session.
    return auth.currentUser !== null;
  }
}

// A persisted Firebase session proves a past admission, not a current one: re-run the server
// gate off the critical path and act on a denial.
function refreshAccessInBackground(api: ApiService, auth: Auth, setServices: SetServices) {
  api.checkAccess().catch((error) => {
    const authDenial = getAuthDenialReason(error);
    if (authDenial) actOnAuthDenial(authDenial, auth, setServices);
  });
}

async function signInWithCustomTokenExchange(api: ApiService, firebaseService: FirebaseService) {
  // Hold the first /auth response's signup fields across every retry: retryWithBackoff re-fetches
  // /auth, which returns isNewUser:false once the user exists, so we capture from the very first
  // response and keep it for whichever auth attempt eventually succeeds.
  let firstAuth: { isNewUser?: boolean; signupDate?: string } | undefined;

  await retryWithBackoff(
    async () => {
      const auth = await api.getFirebaseToken();
      firstAuth ??= { isNewUser: auth.isNewUser, signupDate: auth.signupDate };

      await firebaseService.authenticateWithToken(auth.token);

      // Fire the signup event only after auth succeeds, using the first response's values.
      if (firstAuth.isNewUser && firstAuth.signupDate) {
        capture('user_signed_up', { signup_date: firstAuth.signupDate });
      }
    },
    100,
    30000,
    undefined,
    { shouldRetry: (error) => getAuthDenialReason(error) === null }
  );
}
